import React from 'react';
import {
  Alert, Box, Card, CardContent, Checkbox, Divider, FormControlLabel,
  Grid, MenuItem, Paper, Radio, RadioGroup, Stack, TextField, Typography,
} from '@mui/material';
import PricingPreview from '../PricingPreview.jsx';

const ADDITIONAL_CHARGES = [
  { key: 'stairs', label: 'Stairs' },
  { key: 'longCarry', label: 'Long carry' },
  { key: 'packing', label: 'Packing', requiresService: 'packing' },
  { key: 'heavyItems', label: 'Heavy items' },
  { key: 'specialty', label: 'Specialty items', requiresService: 'specialty' },
  { key: 'assembly', label: 'Disassembly / reassembly', requiresService: 'assembly' },
];

export default function PricingStep({ answers, errors, onChange, token, pricingStatus }) {
  const p = answers.pricing;
  const set = patch => onChange({ ...answers, pricing: { ...p, ...patch } });
  const setAdditional = (key, patch) => set({ additional: { ...p.additional, [key]: { ...p.additional[key], ...patch } } });
  const setLongDistance = patch => set({ longDistance: { ...p.longDistance, ...patch } });

  const hourlyLike = p.chargeType === 'hourly' || p.chargeType === 'both';
  const flatLike = p.chargeType === 'flat' || p.chargeType === 'both';

  return (
    <>
      <Typography variant="h5" fontWeight={600} gutterBottom>How do you charge?</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Answer a few questions and MOVD will configure your pricing for you. You can refine it later.
      </Typography>

      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>HOW DO YOU NORMALLY CHARGE CUSTOMERS?</Typography>
      <RadioGroup row value={p.chargeType} onChange={e => set({ chargeType: e.target.value })} sx={{ mb: 2 }}>
        <FormControlLabel value="hourly" control={<Radio />} label="Hourly" />
        <FormControlLabel value="flat" control={<Radio />} label="Flat rate" />
        <FormControlLabel value="both" control={<Radio />} label="Both" />
      </RadioGroup>
      {errors.rates && <Alert severity="error" sx={{ mb: 2 }}>{errors.rates}</Alert>}

      {hourlyLike && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>HOURLY CREW RATES</Typography>
          <Grid container spacing={2}>
            {answers.crews.crewSizes.map(size => (
              <Grid item xs={6} sm={3} key={size}>
                <TextField
                  fullWidth type="number" inputProps={{ min: 0 }}
                  label={`${size === 5 ? '5+' : size} movers + truck ($/hr)`}
                  value={p.crewRates[size] ?? ''}
                  onChange={e => set({ crewRates: { ...p.crewRates, [size]: e.target.value } })}
                />
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth select required label="Minimum booking" value={p.minimumHours}
            error={!!errors.minimumHours} helperText={errors.minimumHours || 'Smallest job you’ll take'}
            InputLabelProps={{ shrink: true }}
            onChange={e => set({ minimumHours: Number(e.target.value) })}>
            {[2, 3, 4, 5, 6].map(h => <MenuItem key={h} value={h}>{h} hours</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth type="number" inputProps={{ min: 0 }} label="Minimum charge ($)"
            helperText="No quote goes below this"
            InputLabelProps={{ shrink: true }}
            value={p.minimumCharge} onChange={e => set({ minimumCharge: e.target.value })} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth select label="Quote valid for"
            InputLabelProps={{ shrink: true }}
            value={p.quoteValidityDays} onChange={e => set({ quoteValidityDays: Number(e.target.value) })}>
            {[7, 14, 21, 30].map(d => <MenuItem key={d} value={d}>{d} days</MenuItem>)}
          </TextField>
        </Grid>
        {flatLike && (
          <Grid item xs={12} sm={4}>
            <TextField fullWidth type="number" inputProps={{ min: 0 }} label="Booking / base fee ($)"
              InputLabelProps={{ shrink: true }}
              value={p.baseFee} onChange={e => set({ baseFee: e.target.value })} />
          </Grid>
        )}
        <Grid item xs={12} sm={4}>
          <TextField fullWidth type="number" inputProps={{ min: 0 }} label="Per-item handling ($, optional)"
            helperText="Charged per item / box"
            InputLabelProps={{ shrink: true }}
            value={p.perItemRate} onChange={e => set({ perItemRate: e.target.value })} />
        </Grid>
      </Grid>

      {answers.services.longDistance && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>LONG-DISTANCE PRICING</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>How do you price long-distance moves?</Typography>
          {errors.longDistance && <Alert severity="error" sx={{ mb: 1.5 }}>{errors.longDistance}</Alert>}
          <RadioGroup row value={p.longDistance.mode} onChange={e => setLongDistance({ mode: e.target.value })} sx={{ mb: 1.5 }}>
            <FormControlLabel value="per_km" control={<Radio />} label="Per kilometre" />
            <FormControlLabel value="per_mile" control={<Radio />} label="Per mile" />
            <FormControlLabel value="flat" control={<Radio />} label="Flat rate" />
            <FormControlLabel value="custom" control={<Radio />} label="Custom" />
          </RadioGroup>
          {p.longDistance.mode === 'per_km' && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField type="number" inputProps={{ min: 0, step: 0.01 }} label="Rate per kilometre ($)"
                InputLabelProps={{ shrink: true }}
                value={p.longDistance.ratePerKm} onChange={e => setLongDistance({ ratePerKm: e.target.value })} />
              <TextField type="number" inputProps={{ min: 0 }} label="Long-distance minimum charge ($, optional)"
                InputLabelProps={{ shrink: true }}
                value={p.longDistance.minimumCharge} onChange={e => setLongDistance({ minimumCharge: e.target.value })} />
            </Stack>
          )}
          {p.longDistance.mode === 'per_mile' && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField type="number" inputProps={{ min: 0, step: 0.01 }} label="Rate per mile ($)"
                InputLabelProps={{ shrink: true }}
                value={p.longDistance.ratePerMile} onChange={e => setLongDistance({ ratePerMile: e.target.value })} />
              <TextField type="number" inputProps={{ min: 0 }} label="Long-distance minimum charge ($, optional)"
                InputLabelProps={{ shrink: true }}
                value={p.longDistance.minimumCharge} onChange={e => setLongDistance({ minimumCharge: e.target.value })} />
            </Stack>
          )}
          {p.longDistance.mode === 'flat' && (
            <TextField type="number" inputProps={{ min: 0 }} label="Flat rate per long-distance move ($)"
              InputLabelProps={{ shrink: true }}
              value={p.longDistance.flatRate} onChange={e => setLongDistance({ flatRate: e.target.value })} />
          )}
          {p.longDistance.mode === 'custom' && (
            <Alert severity="info">
              No problem — MOVD will use your base rates for long-distance quotes. You can add custom rates from the pricing page after onboarding.
            </Alert>
          )}
        </Paper>
      )}

      <Divider sx={{ my: 3 }} />
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>ADDITIONAL CHARGES</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Turn on anything you charge extra for. Leave off anything you include for free.
      </Typography>
      <Grid container spacing={1.5}>
        {ADDITIONAL_CHARGES.filter(item => !item.requiresService || answers.services[item.requiresService]).map(item => {
          const charge = p.additional[item.key];
          return (
            <Grid item xs={12} sm={6} key={item.key}>
              <Card variant="outlined" sx={{ borderColor: charge.enabled ? 'primary.main' : 'divider' }}>
                <CardContent sx={{ pb: '12px !important' }}>
                  <FormControlLabel
                    control={<Checkbox checked={charge.enabled} onChange={e => setAdditional(item.key, { enabled: e.target.checked })} />}
                    label={<Typography fontWeight={600}>{item.label}</Typography>}
                  />
                  {charge.enabled && (
                    <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
                      {item.key === 'stairs' && (
                        <TextField select size="small" label="How?" value={charge.mode}
                          onChange={e => setAdditional('stairs', { mode: e.target.value })} sx={{ minWidth: 120 }}>
                          <MenuItem value="flat">Flat fee</MenuItem>
                          <MenuItem value="per_floor">Per flight</MenuItem>
                        </TextField>
                      )}
                      <TextField size="small" type="number" inputProps={{ min: 0 }} label="Amount ($)"
                        value={charge.amount} onChange={e => setAdditional(item.key, { amount: e.target.value })} />
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Divider sx={{ my: 3 }} />
      <PricingPreview answers={answers} token={token} pricingStatus={pricingStatus} />
    </>
  );
}
